import { forwardRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  FilterSelectorComponent,
  FilterSelectorBuiltInButtons as Buttons,
  FilterSelectorOperators as Operators,
} from "@etframework/ui.components";
import { FilterSelectorColumnTypes as types } from "@etframework/ui.components";

import { ColumnSettings } from "../../types/FilterSelectorSettings";
import { useBulkUpdateDataHelper } from "../../hooks/BulkUpdateDataHelper";
import { useTaskTypeByBoardId } from "../../data/queries/taskTypeQueries";
import Enums from "../../types/enums";
import { useBoardCustomFields } from "../../data/queries/boardQueries";

export const FilterSelector = forwardRef(
  ({ board, value, id, ...rest }, ref) => {
    const { t, i18n } = useTranslation(["BulkUpdateTasksModal", "Shared"], {
      useSuspense: false,
    });

    const { data: taskTypes } = useTaskTypeByBoardId(board.Id);
    const { data: customFields } = useBoardCustomFields(board.Id);

    const { getDataSource } = useBulkUpdateDataHelper();

    const getColumnSettings = useCallback(() => {
      let columns = Object.values(ColumnSettings).map((x) => ({
        ...x,
        headerText: t("Columns." + (x.headerText ?? x.field)),
      }));

      if (customFields) {
        columns = [
          ...columns,
          ...customFields.map((x) => {
            var type = types.source;
            switch (x.Type) {
              case Enums.CustomFieldDefinitionType.Date:
                type = types.date;
                break;
              case Enums.CustomFieldDefinitionType.Number:
                type = types.number;
                break;
              case Enums.CustomFieldDefinitionType.Boolean:
                type = types.boolean;
                break;
            }

            return {
              field: x.Name,
              type: type,
              displayProperty: "Value",
              headerText: x.Name,
              OData: {
                overrideField: "CustomFields.Value",
                isCollection: true,
                constants: [
                  {
                    field: "CustomFieldId",
                    operator: "eq",
                    value: x.CustomFieldId,
                    settings: {
                      OData: {
                        isCollection: true,
                      },
                    },
                  },
                ],
                isLiteral: true,
              },
            };
          }),
        ];
      }

      if (taskTypes.find((x) => x.Type === Enums.TaskType.TaxReturn)) {
        return columns;
      } else {
        return columns.filter((x) => !x.field.startsWith("TaskTaxReturnInfo"));
      }
    }, [taskTypes, customFields]);

    if (!customFields) return null;

    return (
      <>
        <FilterSelectorComponent
          {...rest}
          ref={ref}
          id={id}
          locale={i18n.language}
          value={value}
          defaultFilters={[
            ColumnSettings.Assignee.field,
            ColumnSettings.Collaborators.field,
            ColumnSettings.Client.field,
            ColumnSettings.DueDateRange.field +
              ":" +
              ColumnSettings.DueDateRange.id,
            ColumnSettings.Tags.field,
            ColumnSettings.Stage.field,
            ColumnSettings.Name.field,
            ColumnSettings.TaxDeadline.field +
              ":" +
              ColumnSettings.TaxDeadline.id,
            ColumnSettings.TaxYear.field,
            ColumnSettings.FilingStatus.field,
          ]}
          filterSettings={{
            [ColumnSettings.Stage.field]: {
              operators: Operators.BaseAmount,
            },
            [ColumnSettings.TaxYear.field]: {
              operators: Operators.BaseAmount,
            },
            [ColumnSettings.Tags.field]: {
              allowCustomValues: false,
            },
            [ColumnSettings.Collaborators.field]: {
              allowCustomValues: false,
            },
            [ColumnSettings.TaxDeadlineExtension.field]: {
              yesLabel: t(
                "BooleanLabels." +
                  ColumnSettings.TaxDeadlineExtension.field +
                  ".true"
              ),
              noLabel: t(
                "BooleanLabels." +
                  ColumnSettings.TaxDeadlineExtension.field +
                  ".false"
              ),
            },
            [ColumnSettings.AutoRollover.field]: {
              yesLabel: t(
                "BooleanLabels." + ColumnSettings.AutoRollover.field + ".true"
              ),
              noLabel: t(
                "BooleanLabels." + ColumnSettings.AutoRollover.field + ".false"
              ),
            },
          }}
          columnSettings={getColumnSettings()}
          getDataSource={getDataSource}
          include={{
            [Buttons.addField]: {
              text: t("FromActions.FilterSelector.AddFilter"),
            },
            [Buttons.resetFilters]: {
              text: t("FromActions.FilterSelector.ResetFilters"),
            },
          }}
        />
      </>
    );
  }
);
